import { ConsoleSqlOutlined } from '@ant-design/icons';
import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { HttpRequest } from '../utils/httpClient';
import config from '../config';

import { createLogger } from '../utils/logger';
const logger = createLogger('contexts');

// Definir la interfaz para el asistente
export interface Assistant {
  id: string;
  accountId: string;
  name: string;
  createdAt: number;
  externalId: string;
  merchantId: string;
  status: number;
  businessName?: string;
  communicationStyle?: string;
  voiceLanguage?: string;
  openAIConfig?: {
    max_tokens?: number;
    model?: string;
    api_key?: string;
  };
  domainTopic?: string;
  specialist?: string;
  fallbackResponse?: string;
  acronyms?: any[];
  advancedConfig?: {
    guardrails: any[];
    businessRules: any[];
  };
  guardrailsConfig?: {
    inputGuards?: string[];
    outputGuards?: string[];
  };
  whatsappMode?: 'direct' | 'groups' | 'both';
  accessPolicy?: 'all' | 'restricted';
}

// Definir la interfaz para el contexto
interface AssistantContextType {
  currentAssistant: Assistant | null;
  availableAssistants: Assistant[];
  setAssistant: (assistantId: string) => void;
  isValidAssistant: (assistantId: string) => boolean;
  loadAssistants: (accountId: string, forceRefresh?: boolean) => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

// Crear el contexto
const AssistantContext = createContext<AssistantContextType | undefined>(undefined);

// Proveedor del contexto
export const AssistantProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentAssistant, setCurrentAssistant] = useState<Assistant | null>(null);
  const [availableAssistants, setAvailableAssistants] = useState<Assistant[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const { accountId, AssistantID } = useParams();
  const navigate = useNavigate();
  const lastAssistantIdRef = useRef<string | null>(null);
  const lastAccountIdRef = useRef<string | null>(null);

  // Cargar los asistentes disponibles para la cuenta actual
  const loadAssistants = useCallback(async (accountId: string, forceRefresh = false) => {
    if (!accountId || (accountId === lastAccountIdRef.current && !forceRefresh)) {
      return;
    }

    setIsLoading(true);
    setError(null);
    lastAccountIdRef.current = accountId;

    try {
      const request = new HttpRequest(`${config.endpoints.assistants}/account/${accountId}`);
      const response = await request.get();

      if (response?.data?.chatbots && Array.isArray(response.data.chatbots)) {
        const assistants = response.data.chatbots;
        setAvailableAssistants(assistants);
        
        // Si hay un asistente seleccionado en la URL, verificar si es válido
        if (AssistantID && AssistantID !== lastAssistantIdRef.current) {
          const assistant = assistants.find(asst => asst.id === AssistantID);
          if (assistant) {
            const new_request = new HttpRequest(`${config.endpoints.chatbot}/info`);
            const new_response = await new_request.get(AssistantID);
            if (new_response?.data?.data) {
              const assistant_info = new_response.data.data;
              setCurrentAssistant(assistant_info);
              lastAssistantIdRef.current = AssistantID;
            }
          } else if (assistants.length > 0) {
            navigate(`/accounts/${accountId}/assistants/${assistants[0].id}/inbox`, { replace: true });
          }
        }
      } else {
        setAvailableAssistants([]);
      }
    } catch (error) {
      logger.error('Error al obtener asistentes:', error);
      setError('Error al cargar los asistentes. Por favor, inténtalo de nuevo.');
      setAvailableAssistants([]);
    } finally {
      setIsLoading(false);
    }
  }, [AssistantID, navigate]);

  // Cargar los asistentes cuando cambia la cuenta
  useEffect(() => {
    if (accountId) {
      loadAssistants(accountId);
    }
  }, [accountId, loadAssistants]);

  // Función para establecer el asistente actual
  const setAssistant = async (assistantId: string) => {
    if (assistantId === lastAssistantIdRef.current) {
      return;
    }

    const assistant = availableAssistants.find(asst => asst.id === assistantId);
    if (assistant) {
      const new_request = new HttpRequest(`${config.endpoints.chatbot}/info`);
      const new_response = await new_request.get(assistantId);
      if (new_response?.data?.data) {
        const assistant_info = new_response.data.data;
        setCurrentAssistant(assistant_info);
        lastAssistantIdRef.current = assistantId;
      }
    }
  };

  // Función para verificar si un asistente es válido
  const isValidAssistant = (assistantId: string) => {
    return availableAssistants.some(asst => asst.id === assistantId);
  };

  return (
    <AssistantContext.Provider
      value={{
        currentAssistant,
        availableAssistants,
        setAssistant,
        isValidAssistant,
        loadAssistants,
        isLoading,
        error
      }}
    >
      {children}
    </AssistantContext.Provider>
  );
};

// Hook personalizado para usar el contexto
export const useAssistant = () => {
  const context = useContext(AssistantContext);
  if (context === undefined) {
    throw new Error('useAssistant must be used within an AssistantProvider');
  }
  return context;
}; 